import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import { favoritesStore } from 'widgets/avia/favorites'
import { tabsChangeSession } from 'widgets/header-tabs/module'

import userStore from 'entities/user/module/user-store'

import { GtagEventKey } from 'shared/constants/analytics'
import { sendGtagEvent } from 'shared/lib/analytics'
import { checkUserAuth } from 'shared/lib/checkUserAuth'
import { getRedirectUrl } from 'shared/lib/getRedirectUrl'
import { AppIcon } from 'shared/ui/app-icon'
import { PagesModule, PagesModuleRouter } from 'shared/ui/tabs-pages'

import { OrdersNavigationIcon } from './ui/orders-navigation-icon'

import { NavigationId, NavigationItem, NavigationItems } from './model'
import s from './styles.module.scss'

interface BottomNavigationProps {
  activeId: NavigationId
}

const navigationClickEvents: Record<NavigationId, GtagEventKey> = {
  home: 'NAV_HOME_CLICK',
  orders: 'NAV_ORDERS_CLICK',
  favorites: 'NAV_FAVORITES_CLICK',
  profile: 'NAV_PROFILE_CLICK',
}

const getHomePath = (routePage: PagesModule): string | null => {
  const routeModulePage = PagesModuleRouter[routePage]?.[routePage]
  return routeModulePage ? (routeModulePage as string) : null
}

export const BottomNavigation = observer(
  ({ activeId }: BottomNavigationProps) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { getPageModule } = tabsChangeSession()
    const userStoreData = userStore.getAccountInfo()

    const guestHasFavorites = userStore.guest_has_favorites

    const hasFavorites = userStoreData
      ? userStoreData?.has_favorites || !!favoritesStore?.favoritesList?.length
      : guestHasFavorites

    const handleItemClick = async (item: NavigationItem) => {
      sendGtagEvent(navigationClickEvents[item.id])

      const routePage = getPageModule() as PagesModule

      if (item.id === 'home') {
        const path = getHomePath(routePage)
        if (path) {
          navigate(path)
        }
        return
      }

      if (
        item.id === 'orders' ||
        item.id === 'profile' ||
        item.id === 'favorites'
      ) {
        const navigatePath = item.path

        if (userStoreData) {
          navigate(navigatePath)
          return
        }

        const redirectUrl = getRedirectUrl(navigatePath)

        try {
          await checkUserAuth({
            redirectUrl,
            onError: () => {},
            onSuccess: () => {
              navigate(navigatePath)
            },
          })
        } catch {
          return
        }
        return
      }

      navigate(item.path)
    }

    return (
      <>
        <nav className={s.container}>
          {NavigationItems.map((item) => {
            const isActive = item.id === activeId
            const isNew = hasFavorites && item.isNew

            return (
              <button
                key={item.id}
                className={clsx(s.item, isActive && s.itemActive)}
                onClick={() => handleItemClick(item)}
                type="button"
              >
                <div className={s.box}>
                  {item.id === 'orders' ? (
                    <OrdersNavigationIcon
                      icon={item.icon}
                      iconClassName={clsx(s.icon, isActive && s.iconActive)}
                      isAuthenticated={Boolean(userStoreData)}
                    />
                  ) : (
                    <AppIcon
                      id={item.icon}
                      className={clsx(s.icon, isActive && s.iconActive)}
                      width={20}
                      height={20}
                    />
                  )}

                  {isNew && <span className={s.new} />}
                </div>

                <span
                  className={clsx(
                    s.label,
                    isActive && s.labelActive,
                    'semi-bold-11',
                  )}
                >
                  {t(item.translationKey)}
                </span>
              </button>
            )
          })}
        </nav>

        <div className={s.emptyWrapper} />
      </>
    )
  },
)
